// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import { assert } from '.';
import { delay } from './delay';

/** Returns false if polling is not complete, and result otherwise. */
export type PollCallback<T> = () => Promise<T | false>;

/**
 * Polls on an async operation every second until a successful result, or polling times out.
 * @param callback Operation to call for result. This should return false if the operation is not yet complete.
 * @returns The result of the last successful callback.
 */
export async function poll<T>(callback: PollCallback<T>, timeoutSeconds = 10): Promise<T> {
    for (let attempt = 1; attempt <= timeoutSeconds; attempt++) {
        try {
            const result = await callback();
            if (result !== false) {
                return result;
            } else if (attempt === timeoutSeconds) {
                throw new Error('Polling timed out.');
            }
        } catch (error) {
            if (attempt === timeoutSeconds) {
                throw error;
            }
        }
        await delay(1000);
    }
    assert.unreachable();
}
